import { useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Box, MenuItem, Select, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchPriceListsResponse, PriceListQuery, fetchPriceLists } from "../../priceList/priceListApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { ProductItem } from "../productsTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists ?? []

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "1rem 5rem 5rem",
        display: "flex",
        flexDirection: "column",
    },
    selectLabel: {
        marginBottom: "0.5rem",
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
    selectBox: {
        marginBottom: "2rem",
    },
    submitButton: {
        alignSelf: "center",
        marginTop: "4rem",
        padding: "0.5rem 1.25rem",
        backgroundColor: tailwindPalette.slate[800],
        "&:hover": {
            backgroundColor: tailwindPalette.slate[900],
        },
    },
    accordion: {
        "&:before": {
            display: "none",
        },
    },
    accordionSummary: {
        padding: "0.5rem 0.75rem 0.5rem 0",
        minHeight: "auto !important",
        borderBottom: `1px solid ${tailwindPalette.slate[400]}`,
        "& .MuiAccordionSummary-content": {
            margin: "0 !important",
        },
    },
    accordionDetails: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "0.25rem",
        padding: "1rem 0 0",
        maxHeight: "12.25rem",
        overflowY: "auto",
    },
    productItem: {
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
    },
}))

type MoveOrCopyToPriceListDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    type: "move" | "copy"
    selectedProducts: ProductItem[]
    onConfirm: (priceListId: number) => void
    isLoading?: boolean
}>

export function MoveOrCopyToPriceListDialog({
    open,
    onClose,
    type,
    selectedProducts,
    onConfirm,
    isLoading = false,
}: MoveOrCopyToPriceListDialogProps) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const [selectedPriceListId, setSelectedPriceListId] = useState<number | "">("")
    const [expanded, setExpanded] = useState(false)

    const { data: priceLists = [] } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: { merchantId },
        select: selectPriceLists,
    })

    const handleConfirm = () => {
        if (selectedPriceListId === "") return
        onConfirm(selectedPriceListId as number)
    }

    const titleId =
        type === "move" ? "products-move-to-price-list-dialog-title" : "products-copy-to-price-list-dialog-title"
    const buttonLabel = type === "move" ? "Move" : "Copy"

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            dialogTitle={
                <LocalizedStrict id={titleId}>
                    <span>{type === "move" ? "Move to price list" : "Copy to price list"}</span>
                </LocalizedStrict>
            }
        >
            <Box className={classes.content}>
                <Box className={classes.selectBox}>
                    <LocalizedStrict id="products-select-price-list-label">
                        <Typography variant="body1" className={classes.selectLabel}>
                            Select price list:
                        </Typography>
                    </LocalizedStrict>
                    <Select
                        fullWidth
                        value={selectedPriceListId}
                        onChange={(e) => setSelectedPriceListId(e.target.value as number)}
                        displayEmpty
                    >
                        {priceLists.map((pl) => (
                            <MenuItem key={pl.id} value={pl.id}>
                                {pl.name}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Accordion
                    expanded={expanded}
                    onChange={() => setExpanded((prev) => !prev)}
                    elevation={0}
                    disableGutters
                    className={classes.accordion}
                >
                    <AccordionSummary
                        className={classes.accordionSummary}
                        expandIcon={<FontAwesomeIcon icon={faChevronDown} color={tailwindPalette.slate[400]} />}
                    >
                        <Typography fontWeight={700}>Products ({selectedProducts.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                        {selectedProducts.map((p) => (
                            <Typography key={p.id} variant="body2" className={classes.productItem}>
                                {p.name}
                            </Typography>
                        ))}
                    </AccordionDetails>
                </Accordion>

                <StandardButton
                    onClick={handleConfirm}
                    disabled={selectedPriceListId === "" || isLoading}
                    updateDefaultClasses={classes.submitButton}
                    fullWidth={false}
                >
                    {buttonLabel}
                </StandardButton>
            </Box>
        </StandardDialog>
    )
}
